import type { Task } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { RepoChip, TaskKey } from '@/components/common/bits';
import { ProgressBar } from '@/components/common/ProgressBar';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { PausedChip } from '@/features/actions/PauseActions';
import { progressFraction, taskNote } from '@/lib/fleet';
import { duration, minutesSince } from '@/lib/format';

/**
 * What the agents are doing, which is the second of PRODUCT.md's three
 * questions.
 *
 * It is a strip rather than a panel because on the home page it is context, not
 * work: nobody acts on it, they glance at it. Nothing renders when nothing is
 * running, because a heading over an empty box is a claim that idle is a state
 * worth a region of the page, and the hero above already said how many are
 * running.
 *
 * A member sees this too. The old page drew the fleet only for admins, which
 * meant most of the company never saw that anything was happening at all between
 * filing a job and being asked to approve its plan.
 */
export function RunningStrip({ tasks }: { tasks: Task[] }) {
  const running = tasks
    .filter((task) => task.status === 'running' || task.status === 'reporting')
    .sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt));

  if (!running.length) return null;

  return (
    <Section>
      <SectionHead
        title="Running now"
        meta={`${running.length} on ${new Set(running.map((task) => task.assignedWorkerName).filter(Boolean)).size || 1} agent${running.length === 1 ? '' : 's'}`}
        actions={
          <Link
            to="/tasks"
            className="text-fg-3 hover:text-foreground text-xs transition-colors duration-100"
          >
            Every task →
          </Link>
        }
      />
      <Surface>
        <ul className="divide-line divide-y">
          {running.map((task) => {
            const fraction = progressFraction(task);
            return (
              <li key={task.id} className="px-gutter py-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
                  <Link
                    to="/tasks/$taskId"
                    params={{ taskId: task.id }}
                    className="hover:text-gate min-w-0 flex-1 truncate text-base transition-colors duration-100"
                  >
                    {task.title}
                  </Link>
                  {task.pausedAt ? <PausedChip /> : null}
                  <RepoChip repo={task.repo} />
                  <span className="text-fg-3 tnum shrink-0 text-xs">
                    {duration(minutesSince(task.startedAt ?? task.updatedAt))}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <ProgressBar fraction={fraction} className="min-w-0 flex-1" />
                  <span className="text-fg-3 min-w-0 shrink-0 truncate text-xs">
                    {taskNote(task) ?? task.assignedWorkerName ?? 'working'}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </Surface>
    </Section>
  );
}
